import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { TitleRun, TitleRunStatus, TitleRunWithSupplements } from '../types/api';

export const titleRunsKeys = {
  all: ['title-runs'] as const,
  list: (projectId: string | null, filters: ListFilters) =>
    [...titleRunsKeys.all, 'list', projectId, filters] as const,
  byId: (projectId: string | null, id: string) =>
    [...titleRunsKeys.all, 'byId', projectId, id] as const,
};

export interface ListFilters {
  tract_id?: string | undefined;
  status?: TitleRunStatus | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useTitleRunsList(filters: ListFilters = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: titleRunsKeys.list(projectId, filters),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ title_runs: TitleRun[] }>('/title-runs', { ...filters });
      return res.title_runs;
    },
  });
}

export function useTitleRunsForTract(tractId: string | null) {
  return useTitleRunsList(tractId ? { tract_id: tractId } : {});
}

export function useTitleRun(id: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: id ? titleRunsKeys.byId(projectId, id) : ['title-runs', 'none'],
    enabled: !!id && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ title_run: TitleRunWithSupplements }>(`/title-runs/${id}`);
      return res.title_run;
    },
  });
}

export interface TitleRunInput {
  tract_id: string;
  title: string;
  sovereignty_date?: string | null | undefined;
  through_date?: string | null | undefined;
  status?: TitleRunStatus | undefined;
  remarks?: string | null | undefined;
}

export function useCreateTitleRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TitleRunInput) => api.post<{ title_run: TitleRun }>('/title-runs', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: titleRunsKeys.all }),
  });
}

export function useUpdateTitleRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Omit<TitleRunInput, 'tract_id'>>) =>
      api.patch<{ title_run: TitleRun }>(`/title-runs/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: titleRunsKeys.all }),
  });
}

export function useDeleteTitleRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/title-runs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: titleRunsKeys.all }),
  });
}

export function useCloseTitleRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ title_run: TitleRun }>(`/title-runs/${id}/close`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: titleRunsKeys.all }),
  });
}

export function useReopenTitleRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ title_run: TitleRun }>(`/title-runs/${id}/reopen`, { confirm: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: titleRunsKeys.all }),
  });
}

export interface SupplementInput {
  title: string;
  remarks?: string | null;
}

export function useCreateSupplement(parentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SupplementInput) =>
      api.post<{ title_run: TitleRun }>(`/title-runs/${parentId}/supplements`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: titleRunsKeys.all }),
  });
}
